import { A2uiComponentEntry, SpaceDocumentLimits, SpaceValidationError } from 'types/space';
import { CompositionDefinition, CompositionRef } from 'types/composition';
import { DEFAULT_SPACE_DOCUMENT_LIMITS } from './limits';

/** Looks up a composition's stored definition by ref; `undefined` means "not a composition reference". */
export type CompositionResolver = (ref: CompositionRef) => CompositionDefinition | undefined;

export interface ExpandCompositionResult {
  components: A2uiComponentEntry[];
  /** id of the expanded root within `components`; unchanged unless the document root was itself a composition ref. */
  root: string;
  errors: SpaceValidationError[];
}

function remapIds(entry: A2uiComponentEntry, idMap: ReadonlyMap<string, string>): A2uiComponentEntry {
  const remapValue = (value: unknown): unknown => {
    if (typeof value === 'string') return idMap.get(value) ?? value;
    if (Array.isArray(value)) return value.map(remapValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, remapValue(val)]));
    }
    return value;
  };

  const remapped: A2uiComponentEntry = { ...entry, id: idMap.get(entry.id) ?? entry.id };
  Object.keys(remapped).forEach((key) => {
    if (key === 'id' || key === 'component') return;
    remapped[key] = remapValue(remapped[key]);
  });
  return remapped;
}

/**
 * A composition's internal subtree binds its own props via `{ $bind: '/props/<name>' }` (see
 * `types/composition.d.ts` `CompositionA2ui`). The referencing entry (`component: '<system_name>'`)
 * carries those prop values as its own extra fields — substitute them in before the subtree is
 * spliced into the document, so an expanded composition renders with the caller's props bound
 * rather than left as unresolved `/props/*` pointers.
 */
function substituteCompositionProps(entry: A2uiComponentEntry, callerProps: Record<string, unknown>): A2uiComponentEntry {
  const substituteValue = (value: unknown): unknown => {
    if (value && typeof value === 'object' && !Array.isArray(value) && typeof (value as { $bind?: unknown }).$bind === 'string') {
      const pointer = (value as { $bind: string }).$bind;
      if (pointer.startsWith('/props/')) {
        return callerProps[pointer.slice('/props/'.length)];
      }
      return value;
    }
    if (Array.isArray(value)) return value.map(substituteValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, substituteValue(val)]));
    }
    return value;
  };

  const substituted: A2uiComponentEntry = { ...entry };
  Object.keys(substituted).forEach((key) => {
    if (key === 'id' || key === 'component') return;
    substituted[key] = substituteValue(substituted[key]);
  });
  return substituted;
}

/**
 * Expands composition references (`component: '<system_name>'`) into their subtree, recursively.
 * Fail-closed: a cyclic composition chain (A -> B -> A) or a post-expansion component count over
 * `maxPostExpansionComponents` aborts expansion with a named error rather than truncating silently.
 */
export function expandComposition(
  a2ui: { components: A2uiComponentEntry[]; root: string },
  resolve: CompositionResolver,
  limits: Partial<SpaceDocumentLimits> = {}
): ExpandCompositionResult {
  const effectiveLimits: SpaceDocumentLimits = { ...DEFAULT_SPACE_DOCUMENT_LIMITS, ...limits };
  const errors: SpaceValidationError[] = [];
  const output: A2uiComponentEntry[] = [];
  let uid = 0;

  function fail(error: SpaceValidationError): null {
    errors.push(error);
    return null;
  }

  // Expands one entry (a plain component, or a composition reference) into `output`, returning the
  // id that now represents it in the expanded tree, or null once an error has been recorded.
  function expandEntry(entry: A2uiComponentEntry, chain: readonly string[]): string | null {
    const def = resolve({ systemName: entry.component, version: entry.componentVersion as number | undefined });

    if (!def) {
      if (output.length >= effectiveLimits.maxPostExpansionComponents) {
        return fail({
          code: 'POST_EXPANSION_SIZE_EXCEEDS_LIMIT',
          message: `Expanded component tree exceeds maximum of ${effectiveLimits.maxPostExpansionComponents} components`,
          path: `a2ui/components/${entry.id}`,
        });
      }
      output.push(entry);
      return entry.id;
    }

    const compositionKey = entry.component;
    if (chain.includes(compositionKey)) {
      return fail({
        code: 'CYCLE_DETECTED',
        message: `Cyclic composition reference detected: ${[...chain, compositionKey].join(' -> ')}`,
        path: `a2ui/components/${entry.id}`,
      });
    }

    if (output.length >= effectiveLimits.maxPostExpansionComponents) {
      return fail({
        code: 'POST_EXPANSION_SIZE_EXCEEDS_LIMIT',
        message: `Expanded component tree exceeds maximum of ${effectiveLimits.maxPostExpansionComponents} components`,
        path: `a2ui/components/${entry.id}`,
      });
    }

    const { id: _id, component: _component, ...callerProps } = entry;
    const nextChain = [...chain, compositionKey];
    const idMap = new Map<string, string>();
    def.a2ui.components.forEach((child) => {
      idMap.set(child.id, `${entry.id}::${child.id}__${uid++}`);
    });

    let expandedRootId: string | null = null;
    for (const child of def.a2ui.components) {
      const resultId = expandEntry(remapIds(substituteCompositionProps(child, callerProps), idMap), nextChain);
      if (resultId === null) return null;
      if (child.id === def.a2ui.root) expandedRootId = resultId;
    }

    if (expandedRootId === null) {
      // The stored definition's `a2ui.root` names no component in its own `a2ui.components`.
      // Returning null without recording an error would abort the whole expansion with an
      // EMPTY error list — the caller sees a silently truncated tree and no signal at all,
      // which is exactly what this function's fail-closed contract promises not to do.
      return fail({
        code: 'ROOT_NOT_FOUND',
        message: `Composition "${compositionKey}" declares root "${def.a2ui.root}", which matches none of its components`,
        path: `a2ui/components/${entry.id}`,
      });
    }

    return expandedRootId;
  }

  // Every top-level entry's original id maps to whatever id now represents it in `output` — itself,
  // unchanged, for a plain component; the composition's expanded root id for a composition ref.
  const topLevelIdMap = new Map<string, string>();
  let expandedRoot = a2ui.root;
  for (const entry of a2ui.components) {
    const resultId = expandEntry(entry, []);
    if (resultId === null) {
      return { components: output, root: a2ui.root, errors };
    }
    topLevelIdMap.set(entry.id, resultId);
    if (entry.id === a2ui.root) expandedRoot = resultId;
  }

  // Sibling entries reference a composition-ref entry by its ORIGINAL top-level id (e.g. a `SpaceGrid`
  // Section listing `children: ['banner-1']`); that id no longer exists once `banner-1` is replaced by
  // its expanded subtree. Rewrite every such reference to the id that now represents it.
  const rewriteSiblingRefs = (value: unknown): unknown => {
    if (typeof value === 'string') {
      const mapped = topLevelIdMap.get(value);
      return mapped !== undefined && mapped !== value ? mapped : value;
    }
    if (Array.isArray(value)) return value.map(rewriteSiblingRefs);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, rewriteSiblingRefs(val)]));
    }
    return value;
  };

  const rewritten = output.map((entry) => {
    const next: A2uiComponentEntry = { ...entry };
    Object.keys(next).forEach((key) => {
      if (key === 'id' || key === 'component') return;
      next[key] = rewriteSiblingRefs(next[key]);
    });
    return next;
  });

  return { components: rewritten, root: expandedRoot, errors };
}
